import React, { useState, useEffect } from 'react';

const DataMonitor = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    const timestamp = new Date().toISOString();
    const newErrors = [];
    const dataEntries = [];

    try {
      // Test basic server connectivity first
      const testResponse = await fetch('/api/test');
      if (!testResponse.ok) {
        throw new Error('Server not responding');
      }
      const testData = await testResponse.json();
      dataEntries.push({
        source: 'Server Test',
        type: 'system',
        data: testData,
        timestamp: timestamp,
        status: 'success'
      });
    } catch (error) {
      newErrors.push({ source: 'Server Test', error: error.message });
      dataEntries.push({
        source: 'Server Test',
        type: 'system',
        data: { error: error.message },
        timestamp: timestamp,
        status: 'error'
      });
    }

    // Test each data feed endpoint using consolidated feeds API
    const endpoints = [
      { url: '/api/feeds?type=earthquakes&limit=5', name: 'USGS Earthquakes' },
      { url: '/api/feeds?type=weather&lat=40.7128&lon=-74.0060&location=NYC', name: 'Weather Data' },
      { url: '/api/feeds?type=reliefweb&limit=5', name: 'ReliefWeb Reports' },
      { url: '/api/feeds?type=news&limit=5', name: 'Crisis News' },
      { url: '/api/feeds?type=economic', name: 'Economic Indicators' },
      { url: '/api/feeds?type=iss', name: 'ISS Position' },
      { url: '/api/feeds?type=covid&country=US', name: 'COVID-19 Data' },
      { url: '/api/feeds?type=demographics&count=3', name: 'Demographics' },
      { url: '/api/feeds?type=status', name: 'System Status' },
      { url: '/api/feeds?type=stats', name: 'Dashboard Stats' }
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint.url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        dataEntries.push({
          source: endpoint.name,
          type: 'data_feed',
          data: data,
          timestamp: timestamp,
          status: 'success',
          url: endpoint.url
        });
      } catch (error) {
        newErrors.push({ source: endpoint.name, error: error.message });
        dataEntries.push({
          source: endpoint.name,
          type: 'data_feed',
          data: { error: error.message },
          timestamp: timestamp,
          status: 'error',
          url: endpoint.url
        });
      }
    }

    setAllData(prev => [...dataEntries, ...prev.slice(0, 50)]); // Keep last 50 entries
    setErrors(newErrors);
    setLastUpdate(timestamp);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    return status === 'success' ? '#84A98C' : '#C85A54'; // sage-500 : coral-500
  };

  const formatData = (data) => {
    if (typeof data === 'string') return data;
    return JSON.stringify(data, null, 2);
  };

  const getDataCount = (data) => {
    if (Array.isArray(data)) return `${data.length} items`;
    if (data && typeof data === 'object' && data.error) return 'Error';
    if (data && typeof data === 'object') return `${Object.keys(data).length} fields`;
    return 'N/A';
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-light mb-4">Live Data Monitor</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-stone-200 p-4 text-center">
            <span className="text-sm font-mono opacity-60">Last Update</span>
            <div className="text-lg font-mono">
              {lastUpdate ? new Date(lastUpdate).toLocaleTimeString() : 'Never'}
            </div>
          </div>
          <div className="border border-stone-200 p-4 text-center">
            <span className="text-sm font-mono opacity-60">Total Entries</span>
            <div className="text-lg font-mono">{allData.length}</div>
          </div>
          <div className="border border-stone-200 p-4 text-center">
            <span className="text-sm font-mono opacity-60">Active Errors</span>
            <div className={`text-lg font-mono ${errors.length > 0 ? 'text-coral-600' : ''}`}>
              {errors.length}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="card-minimal mb-8">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
              <div className="flex items-center justify-center mt-4 opacity-60">
                <div className="w-4 h-4 bg-steel-500 rounded-full mr-2"></div>
                <p>Connecting to data sources...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="card-minimal mb-8 border-coral-200 bg-coral-50">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-5 h-5 bg-coral-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
            <h3 className="text-lg font-serif">Current Errors</h3>
          </div>
          <div className="space-y-2">
            {errors.map((error, index) => (
              <div key={index} className="text-sm">
                <strong>{error.source}:</strong> {error.error}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-minimal">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-sage-500 rounded flex items-center justify-center">
              <div className="w-4 h-3 bg-white rounded-sm"></div>
            </div>
            <h3 className="text-2xl font-serif font-light">Live Data Stream</h3>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchAllData} className="btn flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-600 rounded-full"></div>
              Refresh Now
            </button>
            <div className="flex items-center gap-2 text-sm opacity-60">
              <div className="w-3 h-3 bg-terracotta-500 rounded"></div>
              Auto-refresh: 5 seconds
            </div>
          </div>
        </div>
        
        {allData.length === 0 ? (
          <div className="text-center py-8 opacity-60">
            No data received yet. Checking data sources...
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {allData.slice(0, 10).map((entry, index) => (
              <div key={index} className={`border p-4 ${
                entry.status === 'success' ? 'border-sage-200 bg-sage-50' : 'border-coral-200 bg-coral-50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getStatusColor(entry.status) }}
                    ></span>
                    <span className="font-mono text-sm">{entry.source}</span>
                    <span className="text-xs opacity-60 border border-gray-300 px-2 py-1 rounded">
                      {entry.type}
                    </span>
                    <span className="text-xs font-mono opacity-60">
                      {getDataCount(entry.data)}
                    </span>
                  </div>
                  <div className="text-xs opacity-60">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </div>
                </div>
                {entry.url && (
                  <div className="text-xs opacity-60 mb-2 font-mono">
                    {entry.url}
                  </div>
                )}
                <div className="bg-white border border-stone-200 p-3 text-xs font-mono overflow-x-auto">
                  <pre className="whitespace-pre-wrap">
                    {typeof entry.data === 'object' ? 
                      JSON.stringify(entry.data, null, 2).slice(0, 500) + 
                      (JSON.stringify(entry.data).length > 500 ? '...' : '') : 
                      entry.data
                    }
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataMonitor;